import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Badge, Alert, Modal, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faCheck, 
  faTimes, 
  faEye, 
  faSignOutAlt,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faBuilding,
  faImage
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { upgradeRequestService } from '../../services/upgradeRequestService';
import { contractService } from '../../services/contractService';
import { companyService } from '../../services/companyService';

const UpgradeRequests = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchUpgradeRequests();
  }, []);

  const fetchUpgradeRequests = async () => {
    try {
      setLoading(true);
      const requestsData = await upgradeRequestService.getAllUpgradeRequests();
      
      // Fetch company details for each request
      const requestsWithCompany = await Promise.all(
        requestsData.map(async (request) => {
          try {
            const company = await companyService.getCompanyById(request.companyId);
            return { ...request, company };
          } catch (error) {
            console.error('Error fetching company:', error);
            return { ...request, company: null };
          }
        })
      );
      
      setRequests(requestsWithCompany);
    } catch (error) {
      console.error('Error fetching upgrade requests:', error);
      setError('Failed to load upgrade requests');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', text: 'Pending', icon: faClock },
      approved: { variant: 'success', text: 'Approved', icon: faCheckCircle },
      rejected: { variant: 'danger', text: 'Rejected', icon: faTimesCircle }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status, icon: faClock };
    return (
      <Badge bg={config.variant}>
        <FontAwesomeIcon icon={config.icon} className="me-1" />
        {config.text}
      </Badge>
    );
  };

  const getFeatureIcon = (feature) => {
    const featureIcons = {
      logo_upload: faImage,
      advanced_analytics: faBuilding
    };
    return featureIcons[feature] || faBuilding;
  };

  const getFeatureName = (feature) => {
    const featureNames = {
      logo_upload: 'Logo Upload',
      advanced_analytics: 'Advanced Analytics'
    };
    return featureNames[feature] || feature;
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes || '');
    setShowDetailsModal(true);
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      
      // Update the request status
      await upgradeRequestService.updateUpgradeRequestStatus(
        selectedRequest.id, 
        'approved', 
        adminNotes
      );

      // If it's a logo upload request, enable the logo feature for the company
      if (selectedRequest.feature === 'logo_upload') {
        // First get the existing settings
        const existingSettings = await contractService.getBrokerageSettings(selectedRequest.companyId);
        
        // Update only the logoEnabled field, keeping all other fields unchanged
        await contractService.updateBrokerageSettings(
          selectedRequest.companyId,
          existingSettings.brokeragePercentage || 5,
          existingSettings.currency || 'USD',
          existingSettings.logoUrl || '',
          true // enable logo
        );
      }

      setSuccess('Request approved successfully!');
      setShowDetailsModal(false);
      fetchUpgradeRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      setError('Failed to approve request: ' + (error.message || error));
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      
      await upgradeRequestService.updateUpgradeRequestStatus(
        selectedRequest.id, 
        'rejected', 
        adminNotes
      );

      setSuccess('Request rejected successfully!');
      setShowDetailsModal(false);
      fetchUpgradeRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      setError('Failed to reject request: ' + (error.message || error));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request? This action cannot be undone.')) {
      try {
        await upgradeRequestService.deleteUpgradeRequest(requestId);
        setSuccess('Request deleted successfully!');
        fetchUpgradeRequests();
      } catch (error) {
        console.error('Error deleting request:', error);
        setError('Failed to delete request: ' + (error.message || error));
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/admin/dashboard')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
          <div>
            <h2 className="mb-1">Upgrade Requests</h2>
            <p className="text-muted mb-0">Manage feature upgrade requests from property managers</p>
          </div>
        </div>
        <Button variant="outline-danger" onClick={handleLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
          Logout
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h4 className="text-warning">{requests.filter(r => r.status === 'pending').length}</h4>
              <p className="text-muted mb-0">Pending Requests</p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h4 className="text-success">{requests.filter(r => r.status === 'approved').length}</h4>
              <p className="text-muted mb-0">Approved</p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h4 className="text-danger">{requests.filter(r => r.status === 'rejected').length}</h4>
              <p className="text-muted mb-0">Rejected</p>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h4>{requests.length}</h4>
              <p className="text-muted mb-0">Total Requests</p>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Requests Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">Upgrade Requests</h5>
        </Card.Header>
        <Card.Body>
          {requests.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faBuilding} className="text-muted mb-3" size="3x" />
              <h5>No upgrade requests found</h5>
              <p className="text-muted">No property managers have submitted upgrade requests yet.</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Feature</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <div>
                        <strong>{request.company?.name || 'Unknown Company'}</strong>
                        <div className="text-muted small">{request.company?.email || 'N/A'}</div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon 
                          icon={getFeatureIcon(request.feature)} 
                          className="text-primary me-2" 
                        />
                        {getFeatureName(request.feature)}
                      </div>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '200px' }}>
                        {request.description || 'No description provided'}
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(request.status)}
                    </td>
                    <td>
                      <div className="text-muted small">
                        {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleViewDetails(request)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        {request.status === 'pending' && (
                          <>
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              onClick={() => handleViewDetails(request)}
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleViewDetails(request)}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </>
                        )}
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteRequest(request.id)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <h6>Company Information</h6>
                  <p><strong>Name:</strong> {selectedRequest.company?.name || 'Unknown'}</p>
                  <p><strong>Email:</strong> {selectedRequest.company?.email || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedRequest.company?.phone || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                  <h6>Request Information</h6>
                  <p><strong>Feature:</strong> {getFeatureName(selectedRequest.feature)}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedRequest.status)}</p>
                  <p><strong>Requested:</strong> {selectedRequest.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}</p>
                </div>
              </div>
              
              <div className="mb-3">
                <h6>Description</h6>
                <p className="text-muted">
                  {selectedRequest.description || 'No description provided'}
                </p>
              </div>

              {selectedRequest.status === 'pending' && (
                <div className="mb-3">
                  <h6>Admin Notes</h6>
                  <Form.Control
                    as="textarea"
                    rows="3"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this request (optional)"
                  />
                </div>
              )}

              {selectedRequest.adminNotes && (
                <div className="mb-3">
                  <h6>Previous Admin Notes</h6>
                  <p className="text-muted">{selectedRequest.adminNotes}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button 
                variant="success" 
                onClick={handleApproveRequest}
                disabled={processing}
              >
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                {processing ? 'Approving...' : 'Approve'}
              </Button>
              <Button 
                variant="danger" 
                onClick={handleRejectRequest}
                disabled={processing}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                {processing ? 'Rejecting...' : 'Reject'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UpgradeRequests; 